// src/store/six-detail/sixDetailSelectors.ts
import { createSelector } from "@reduxjs/toolkit";

import type { RootState } from "@/store";
import type { FileMetadata } from "@/types/poc/helia";
import { mapSixFilesToFileMetadata, mapSixFoldersToFileMetadata } from "@/store/mappers/mapSixFileToFileMetadata";
import { isDirectChildFolder, isFileInCurrentPath } from "@/utils/folderPath.utils";

export const selectSixDetailState = (state: RootState) => state.sixDetail;

export const selectSixId = createSelector(
  selectSixDetailState,
  (state) => state.sixId
);

export const selectFiles = createSelector(
  selectSixDetailState,
  (state) => state.files
);

export const selectActivity = createSelector(
  selectSixDetailState,
  (state) => state.activity
);

export const selectParticipants = createSelector(
  selectSixDetailState,
  (state) => state.participants
);

export const selectLoadingFiles = createSelector(
  selectSixDetailState,
  (state) => state.loadingFiles
);

export const selectLoadingActivity = createSelector(
  selectSixDetailState,
  (state) => state.loadingActivity
);

export const selectUploadProgress = createSelector(
  selectSixDetailState,
  (state) => state.uploadProgress
);

export const selectSixDetailError = createSelector(
  selectSixDetailState,
  (state) => state.error
);

export const selectSixDetailMeta = createSelector(
  selectSixDetailState,
  (state) => state.meta
)
export const selectSixDetailLoading = (state: RootState) =>
  state.sixDetail.loadingFiles || state.sixDetail.loadingActivity;

/**
 * Selector factory that returns files from sixDetailSlice as FileMetadata[].
 * Merges API-sourced files with local Helia state for enrichment.
 *
 * @param sixId - The Six ID to get files for
 * @returns Memoized selector returning FileMetadata[]
 * @deprecated Use selectCurrentFolderContents for folder-aware file listing
 */
export const selectFilesAsFileMetadata = (sixId: string) =>
  createSelector(
    [selectSixDetailState, (state: RootState) => state.helia.files],
    (sixDetailState, heliaFiles) => {
      const sixFiles = sixDetailState.files;
      const mappedFiles = mapSixFilesToFileMetadata(sixFiles, sixId);

      // Enrich with local Helia data when available (e.g., actual addedBy from P2P)
      return mappedFiles.map((file) => {
        const localFile = heliaFiles[file.cid] as FileMetadata | undefined;
        if (localFile !== undefined) {
          return { ...file, ...localFile };
        }
        return file;
      });
    }
  );

// ============================================================================
// Folder Navigation Selectors
// ============================================================================

/**
 * Select all folders from state
 */
export const selectFolders = createSelector(
  selectSixDetailState,
  (state) => state.folders
);

/**
 * Select current folder path
 */
export const selectCurrentPath = createSelector(
  selectSixDetailState,
  (state) => state.currentPath
);

/**
 * Check if currently at root path
 */
export const selectIsAtRoot = createSelector(
  selectCurrentPath,
  (currentPath) => currentPath === '/'
);

/**
 * Select contents of current folder as FileMetadata[].
 * Returns folders first, then files, all filtered to current path.
 * Enriches files with local Helia data when available.
 *
 * @param sixId - The Six ID to get contents for
 * @returns Memoized selector returning FileMetadata[] with folders first
 */
export const selectCurrentFolderContents = (sixId: string) =>
  createSelector(
    [
      selectSixDetailState,
      selectCurrentPath,
      (state: RootState) => state.sixDetail.files,
    ],
    (sixDetailState, currentPath, heliaFiles) => {
      // Filter files in current folder
      const filesInFolder = sixDetailState.files.filter(
        (file) => isFileInCurrentPath(file.path, currentPath)
      );

      // Filter folders that are direct children of current path
      const foldersInPath = sixDetailState.folders.filter(
        (folder) => isDirectChildFolder(folder.path, currentPath)
      );

      // Map to FileMetadata
      const mappedFiles = mapSixFilesToFileMetadata(filesInFolder, sixId);
      const mappedFolders = mapSixFoldersToFileMetadata(foldersInPath, sixId);

      // Return folders first, then files
      return [...mappedFolders, ...mappedFiles];
    }
  );
